class LyleSizeGuides extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();

    this.#initNavigation();
    this.#initLookup();
    this.#initUnitToggle();
    this.#setInitialActive();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  get #signal() {
    return this.#controller?.signal;
  }

  get #navLinks() {
    return [...this.querySelectorAll("[data-size-guides-nav]")];
  }

  get #sections() {
    return [...this.querySelectorAll("[data-size-guides-section]")];
  }

  // --- Navigation -----------------------------------------------------------

  #initNavigation() {
    for (const link of this.#navLinks) {
      link.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          this.#activate(link.dataset.sizeGuidesNav, { scroll: true });
        },
        { signal: this.#signal },
      );
    }

    // Back/forward navigation changes the hash without a click — activate so is-hidden is removed.
    window.addEventListener(
      "hashchange",
      (e) => {
        let hash = '';
        try { hash = new URL(e.newURL).hash.slice(1); } catch { hash = ''; }
        const handles = this.#navLinks.map((link) => link.dataset.sizeGuidesNav);
        if (handles.includes(hash)) {
          this.#activate(hash, { scroll: false, updateHash: false });
        }
      },
      { signal: this.#signal },
    );
  }

  #setInitialActive() {
    const handles = this.#navLinks.map((link) => link.dataset.sizeGuidesNav);
    if (handles.length === 0) return;

    const hash = window.location.hash.replace("#", "");
    const target = handles.includes(hash) ? hash : handles[0];

    this.#activate(target, { scroll: false, updateHash: false });
  }

  #activate(handle, { scroll, updateHash = true }) {
    if (!handle) return;

    for (const link of this.#navLinks) {
      link.classList.toggle(
        "is-active",
        link.dataset.sizeGuidesNav === handle,
      );
    }

    for (const section of this.#sections) {
      section.classList.toggle(
        "is-hidden",
        section.dataset.sizeGuidesSection !== handle,
      );
    }

    if (updateHash) {
      const url = new URL(window.location.href);
      url.hash = handle;
      window.history.replaceState({}, "", url.href);
    }

    if (scroll) {
      const content = this.querySelector("[data-size-guides-content]");
      content?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  // --- Lookup ---------------------------------------------------------------

  #initLookup() {
    const categorySelect = this.querySelector("[data-size-guides-category]");
    const subcategorySelect = this.querySelector(
      "[data-size-guides-subcategory]",
    );
    const button = this.querySelector("[data-size-guides-lookup]");

    if (!categorySelect || !subcategorySelect || !button) return;

    categorySelect.addEventListener(
      "change",
      () => {
        this.#populateSubcategories(categorySelect.value, subcategorySelect);
        button.disabled = true;
      },
      { signal: this.#signal },
    );

    subcategorySelect.addEventListener(
      "change",
      () => {
        button.disabled = subcategorySelect.value === "";
      },
      { signal: this.#signal },
    );

    button.addEventListener(
      "click",
      () => {
        const category = categorySelect.value;
        const subcategory = subcategorySelect.value;
        if (!category || !subcategory) return;

        this.#activate(category, { scroll: false });

        const target = this.querySelector(
          `[data-size-guides-title="${subcategory}"]`,
        );
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
      },
      { signal: this.#signal },
    );
  }

  #populateSubcategories(categoryHandle, subcategorySelect) {
    const placeholder = subcategorySelect.querySelector("option[value='']");
    subcategorySelect.replaceChildren();
    if (placeholder) {
      placeholder.selected = true;
      subcategorySelect.append(placeholder);
    }

    const section = this.querySelector(
      `[data-size-guides-section="${categoryHandle}"]`,
    );
    if (!section) {
      subcategorySelect.disabled = true;
      return;
    }

    const items = section.querySelectorAll("[data-size-guides-title]");
    for (const item of items) {
      const option = document.createElement("option");
      option.value = item.dataset.sizeGuidesTitle;
      option.textContent =
        item.dataset.sizeGuidesTitleLabel || item.dataset.sizeGuidesTitle;
      subcategorySelect.append(option);
    }

    subcategorySelect.disabled = items.length === 0;
  }

  // --- Unit toggle (shared across every item) -------------------------------

  #initUnitToggle() {
    const inputs = this.querySelectorAll("[data-size-guides-unit]");

    for (const input of inputs) {
      input.addEventListener(
        "change",
        () => {
          if (input.checked) this.#setUnit(input.value);
        },
        { signal: this.#signal },
      );
    }
  }

  #setUnit(unit) {
    if (unit !== "cm" && unit !== "in") return;

    this.dataset.unit = unit;

    for (const input of this.querySelectorAll("[data-size-guides-unit]")) {
      const isMatch = input.value === unit;
      input.checked = isMatch;

      const label = input.nextElementSibling;
      label?.classList.toggle("is-active", isMatch);
    }

    requestAnimationFrame(() => {
      for (const table of this.querySelectorAll(
        `[data-size-guides-unit-panel="${unit}"] lyle-csv-table`,
      )) {
        table.refreshScrollbar?.();
      }
    });
  }
}

if (!customElements.get("lyle-size-guides")) {
  customElements.define("lyle-size-guides", LyleSizeGuides);
}
